import { effect, signal } from "alien-signals";
import type { Signal } from "#/use-signal";

/**
 * Configuration for creating a URL filter store.
 */
export interface UrlFilterConfig<T> {
  /** URL parameter name (e.g., "book_id", "source") */
  param: string;
  /** Default value when param is not present in URL */
  defaultValue?: T;
  /** Convert value to URL string format */
  serialize?: (value: T) => string | string[];
  /** Convert URL string/value to typed value */
  deserialize?: (value: string | string[]) => T;
  /** Support multiple values with same param (e.g., ?book_id=1&book_id=2) */
  multi?: boolean;
}

// Default serializers for common types
const defaultSerializers = {
  string: (v: string): string => v,
  number: (v: number): string => v.toString(),
  boolean: (v: boolean): string => v.toString(),
  stringArray: (v: string[]): string[] => v,
  numberArray: (v: number[]): string[] => v.map((n) => n.toString()),
};

// Default deserializers for common types
const defaultDeserializers = {
  string: (v: string): string => v,
  number: (v: string): number => Number(v),
  boolean: (v: string): boolean => v === "true",
  stringArray: (v: string[]): string[] => v,
  numberArray: (v: string[]): number[] => v.map(Number),
};

// Global state to prevent circular updates
let isUpdatingFromUrl = false;

// Store registration for popstate handling
// We use unknown internally and cast when accessing
interface StoreEntry {
  signal: Signal<unknown>;
  config: UrlFilterConfig<unknown>;
  initialValue: unknown;
  readFromUrl: () => unknown;
}

const registeredStores = new Map<string, StoreEntry>();

// React Router setSearchParams function (set from components)
let setSearchParamsFn: ((params: URLSearchParams) => void) | null = null;

/**
 * Set the React Router setSearchParams function for URL updates.
 * Call this from a component that has access to useSearchParams().
 */
export function setUrlSearchParamsSetter(fn: ((params: URLSearchParams) => void) | null) {
  setSearchParamsFn = fn;
}

/**
 * Read current value from URL for a given config.
 */
function readFromUrl<T>(config: UrlFilterConfig<T>): T {
  const searchParams = new URLSearchParams(window.location.search);

  if (config.multi) {
    const values = searchParams.getAll(config.param);
    if (values.length === 0) {
      return (config.defaultValue ?? []) as T;
    }
    const deserializer =
      config.deserialize ?? (defaultDeserializers.stringArray as (v: string | string[]) => T);
    return deserializer(values);
  } else {
    const value = searchParams.get(config.param);
    if (value === null) {
      return (config.defaultValue ?? null) as T;
    }
    const deserializer =
      config.deserialize ?? (defaultDeserializers.string as (v: string | string[]) => T);
    return deserializer(value);
  }
}

/**
 * Write a value to URL for a given config.
 */
function writeToUrl<T>(config: UrlFilterConfig<T>, value: T): URLSearchParams {
  const searchParams = new URLSearchParams(window.location.search);

  if (config.multi) {
    searchParams.delete(config.param);
    if (value === null || value === undefined) {
      // Keep deleted
    } else {
      const serializer =
        config.serialize ?? (defaultSerializers.stringArray as (v: T) => string | string[]);
      const serialized = serializer(value);
      const values = Array.isArray(serialized) ? serialized : [serialized];
      values.forEach((v) => searchParams.append(config.param, v));
    }
  } else {
    if (value === null || value === undefined || value === "") {
      searchParams.delete(config.param);
    } else {
      const serializer =
        config.serialize ?? (defaultSerializers.string as (v: T) => string | string[]);
      const serialized = serializer(value);
      const strValue = Array.isArray(serialized) ? serialized[0] : serialized;
      if (strValue !== undefined) {
        searchParams.set(config.param, strValue);
      }
    }
  }

  return searchParams;
}

/**
 * Initialize URL filter sync to handle browser back/forward navigation.
 * This should be called once at app initialization.
 */
export function initializeUrlFilterSync() {
  window.addEventListener("popstate", () => {
    isUpdatingFromUrl = true;

    // Update all registered stores from URL
    for (const [param, entry] of registeredStores) {
      const newValue = entry.readFromUrl();
      entry.signal(newValue);
    }

    isUpdatingFromUrl = false;
  });
}

/**
 * Create a URL filter store signal that syncs with URL query parameters.
 *
 * @example
 * ```ts
 * // Single string value
 * export const sourceFilterSignal = createUrlFilterStore<string | null>({
 *   param: "source",
 *   defaultValue: null,
 * });
 *
 * // Multi-value number array
 * export const bookIdFilterSignal = createUrlFilterStore<number[]>({
 *   param: "book_id",
 *   defaultValue: [],
 *   multi: true,
 *   serialize: (ids) => ids.map(id => id.toString()),
 *   deserialize: (strs) => strs.map(Number),
 * });
 * ```
 */
export function createUrlFilterStore<T>(config: UrlFilterConfig<T>): Signal<T> {
  // Prevent duplicate registrations
  if (registeredStores.has(config.param)) {
    return registeredStores.get(config.param)!.signal as Signal<T>;
  }

  // Get initial value from URL
  const initialValue = readFromUrl(config);

  // Create signal
  const filterSignal = signal<T>(initialValue);

  // Register store
  registeredStores.set(config.param, {
    signal: filterSignal as Signal<unknown>,
    config: config as UrlFilterConfig<unknown>,
    initialValue,
    readFromUrl: () => readFromUrl(config),
  });

  // Sync signal changes to URL
  effect(() => {
    // Prevent circular updates during popstate
    if (isUpdatingFromUrl) return;

    const value = filterSignal();
    const newParams = writeToUrl(config, value);

    // Use React Router's setSearchParams if available (from component context)
    if (setSearchParamsFn) {
      setSearchParamsFn(newParams);
    } else {
      // Fallback to history API
      const newUrl = `${window.location.pathname}?${newParams.toString()}`;
      window.history.replaceState(null, "", newUrl);
    }
  });

  return filterSignal;
}

/**
 * Get all registered filter parameters as a URLSearchParams object.
 * Useful for constructing URLs with all active filters.
 */
export function getAllFilterParams(): URLSearchParams {
  const params = new URLSearchParams();

  for (const [param, entry] of registeredStores) {
    const value = entry.signal();
    const updatedParams = writeToUrl(entry.config, value);
    // Merge params
    for (const [key, val] of updatedParams.entries()) {
      params.append(key, val);
    }
  }

  return params;
}

/**
 * Reset a specific filter to its default value.
 */
export function resetFilter<T>(signal: Signal<T>, defaultValue: T): void {
  signal(defaultValue);
}

/**
 * Reset all registered filters to their default values.
 */
export function resetAllFilters(): void {
  for (const [param, entry] of registeredStores) {
    const signal = entry.signal as Signal<unknown>;
    signal(entry.initialValue);
  }
}
